/**
 * Product and blog card reveal — a soft rise from below with a fade, once per
 * card, on the home page and collection pages.
 *
 * NOT to be confused with `assets/maahi-reveal.js`, which is the THEME's own
 * reveal driver: it adds `.is-visible` to `.maahi-reveal` elements (the hero
 * eyebrow, heading, subheading and CTA) and is loaded from
 * snippets/scripts.liquid. This file was briefly created under that name on
 * 8 Sep 2026 and silently overwrote it, which left the hero text and Explore
 * button stuck at opacity 0. Keep these two filenames distinct.
 *
 * READ THIS BEFORE CHANGING THE CLASSES.
 *
 * The hidden starting state is CSS, keyed on `html.maahi-reveal-on
 * product-card:not(.is-revealed)`, and the guard class is added by a small
 * INLINE script in the head — deliberately not here. This file is deferred, so
 * by the time it runs the cards have already painted; adding the hidden state
 * from here would make each card appear at full opacity, fade OUT, then fade
 * back in. The hidden state has to exist before the first paint.
 *
 * That head script also carries a 2.5s failsafe which drops the guard class
 * unless this file confirms it is alive, so a blocked or broken script can
 * never leave the catalogue invisible. Setting `data-reveal-ready` below is
 * what makes that promise — do not remove it.
 *
 * Three classes, each doing one job:
 *   (none)         hidden, because of :not(.is-revealed)
 *   .is-revealing  carries the transition, for the duration of the animation
 *   .is-revealed   final state; also what stops it being hidden again
 *
 * `.is-revealing` is stripped afterwards so no transition of ours is left on
 * the card — the theme puts its own `transform .25s ease-out` there for the
 * hover lift, and ours would otherwise sit on top of it at 620ms.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  /* The head script only adds the guard when it intends to animate. If it is
     absent there is nothing to do and, importantly, nothing is hidden. */
  if (!root.classList.contains('maahi-reveal-on')) return;

  /* Tells the failsafe this file is alive, so the guard class stays. */
  root.setAttribute('data-reveal-ready', '');

  /* Blog cards ride along with the product cards. On a laptop the journal is a
     static five-across row with no carousel, so this reveal is the only motion
     it has; on mobile the cards reveal together as the section scrolls in and
     the carousel takes over from there. The reveal transform sits on the card
     while the hover lift is on `.maahi-bc__link` inside it, so they never
     fight. */
  var SELECTOR = 'product-card, .maahi-bc__card';
  var STAGGER = 70;   /* ms between neighbours revealed together */
  var MAX_STEPS = 6;  /* so a 24-card grid never waits ~1.7s for the last one */

  /* Reveal one card. Pulled out of the observer callback so the cards that are
     already on screen at load can be revealed directly, without waiting for an
     IntersectionObserver callback that may never arrive. */
  function reveal(card, delay) {
      card.classList.add('is-revealing');
      card.style.transitionDelay = delay + 'ms';

      /* Two frames: one for the browser to paint the hidden state with the
         transition now attached, one to change the value so it animates
         rather than jumping. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          card.classList.add('is-revealed');
        });
      });

      var done = false;
      function finish() {
        if (done) return;
        done = true;
        card.removeEventListener('transitionend', finish);
        /* Add .is-revealed here too, not only in the rAF above. In a background
           tab requestAnimationFrame never fires, so without this the timeout
           below would strip .is-revealing from a card that had never gained
           .is-revealed — and :not(.is-revealed) would leave it hidden for good.
           Setting it here makes the final state unconditional. */
        card.classList.add('is-revealed');
        card.classList.remove('is-revealing');
        card.style.transitionDelay = '';
      }
      card.addEventListener('transitionend', finish);
      /* transitionend does not fire if the transition is interrupted or the
         tab is hidden while it runs, and a card left mid-reveal would be a
         product nobody can see. */
      setTimeout(finish, delay + 1400);
  }

  var observer = new IntersectionObserver(function (entries) {
    var shown = 0;
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      reveal(entry.target, (shown % MAX_STEPS) * STAGGER);
      shown++;
    });
  }, {
    /* Begin a little before the card arrives, so the movement is finishing as
       it comes into view rather than starting once it is already there. */
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.05
  });

  /* Anything already on screen is revealed straight away rather than handed to
     the observer. Two reasons: the above-the-fold cards are the ones that must
     never fail to appear, and it removes any dependency on the observer firing
     for the cards a visitor sees first. */
  function onScreen(el) {
    var b = el.getBoundingClientRect();
    return b.top < (window.innerHeight || 0) * 0.95 && b.bottom > 0;
  }

  function watch() {
    var cards = document.querySelectorAll(SELECTOR);
    var immediate = 0;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.hasAttribute('data-reveal-seen')) continue;
      card.setAttribute('data-reveal-seen', '');

      if (onScreen(card)) {
        reveal(card, (immediate % MAX_STEPS) * STAGGER);
        immediate++;
      } else {
        observer.observe(card);
      }
    }
  }

  /* A scroll-driven sweep alongside the observer. The observer is the efficient
     path, but it is the ONLY thing standing between a below-the-fold product
     and never being seen, so this makes it optional: anything that scrolls into
     view and has not revealed gets revealed here regardless. Throttled with a
     flag rather than a timer so it costs almost nothing per scroll event. */
  var sweeping = false;
  function sweep() {
    if (sweeping) return;
    sweeping = true;
    requestAnimationFrame(function () { sweeping = false; });
    var cards = document.querySelectorAll('product-card:not(.is-revealed), .maahi-bc__card:not(.is-revealed)');
    var n = 0;
    for (var i = 0; i < cards.length; i++) {
      if (!onScreen(cards[i])) continue;
      observer.unobserve(cards[i]);
      reveal(cards[i], (n % MAX_STEPS) * STAGGER);
      n++;
    }
  }

  window.addEventListener('scroll', sweep, { passive: true });
  window.addEventListener('resize', sweep, { passive: true });

  /* Last line of defence. If nothing has revealed a few seconds in — an
     observer that never fired, an exception, anything unforeseen — drop the
     guard class so every product is simply visible. An unanimated grid is a
     small loss; an invisible catalogue is not. */
  setTimeout(function () {
    if (!document.querySelector('product-card.is-revealed, .maahi-bc__card.is-revealed')) {
      root.classList.remove('maahi-reveal-on');
    }
  }, 4000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }

  /* Collection pages swap the grid when a filter or sort is applied, and the
     theme editor re-renders sections; both bring in cards never seen before.
     Those arrive hidden, so they must be picked up or they stay that way. */
  document.addEventListener('shopify:section:load', watch);

  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function () {
      if (pending) clearTimeout(pending);
      pending = setTimeout(watch, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
